import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Link as LinkIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { log } from '../../utils/errors/logger';
import { formatDate } from '../../utils/formatting/formatters';
import {
  useIdentityLinkStatus,
  useStartIdentityLink,
  useUnlinkIdentity,
  type IdentityLinkInfo,
  type AvailableLinkProvider,
} from '../../hooks/api/useIdentityLink';

type ProviderRow =
  | { kind: 'linked'; providerKey: string; displayName: string; linkedAt?: string | null }
  | { kind: 'available'; providerKey: string; displayName: string };

const LinkedAccountsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showError } = useSnackbar();

  const { data: status, isLoading } = useIdentityLinkStatus();
  const startLink = useStartIdentityLink();
  const unlink = useUnlinkIdentity();

  const [unlinkConfirm, setUnlinkConfirm] = useState<ProviderRow | null>(null);

  const rows: ProviderRow[] = [
    ...Object.values(status?.identities ?? {}).map((ident: IdentityLinkInfo) => ({
      kind: 'linked' as const,
      providerKey: ident.provider_key,
      displayName: ident.display_name,
      linkedAt: ident.linked_at,
    })),
    ...((status?.available_providers ?? []) as AvailableLinkProvider[]).map((p) => ({
      kind: 'available' as const,
      providerKey: p.provider_key,
      displayName: p.display_name,
    })),
  ];

  const handleStart = async (providerKey: string) => {
    try {
      await startLink.mutateAsync(providerKey);
      // onSuccess in the hook performs the redirect to the IdP — nothing
      // more to do here; the user leaves this page.
    } catch (error) {
      log.error('Failed to start identity link', { error });
      showError(t('settings.linkedAccounts.error'));
    }
  };

  const handleUnlink = async () => {
    if (!unlinkConfirm) { return; }
    try {
      await unlink.mutateAsync(unlinkConfirm.providerKey);
    } catch (error) {
      log.error('Failed to unlink identity', { error });
      showError(t('settings.linkedAccounts.error'));
    } finally {
      setUnlinkConfirm(null);
    }
  };

  return (
    <>
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LinkIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6">
            {t('settings.linkedAccounts.title')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('settings.linkedAccounts.description')}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('settings.linkedAccounts.noProviders')}
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {rows.map((row) => {
              const isLinked = row.kind === 'linked';
              const startPending = startLink.isPending && startLink.variables === row.providerKey;
              const unlinkPending = unlink.isPending && unlink.variables === row.providerKey;
              return (
                <Box
                  key={row.providerKey}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">{row.displayName}</Typography>
                    {isLinked && row.linkedAt ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('settings.linkedAccounts.linkedAt')}:{' '}
                        {formatDate(row.linkedAt, i18n.language)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('settings.linkedAccounts.notLinked')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      label={isLinked
                        ? t('settings.linkedAccounts.linked')
                        : t('settings.linkedAccounts.notLinked')}
                      color={isLinked ? 'success' : 'default'}
                    />
                    {isLinked ? (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={unlinkPending ? <CircularProgress size={16} /> : <LinkOffIcon />}
                        disabled={unlinkPending}
                        onClick={() => setUnlinkConfirm(row)}
                      >
                        {t('settings.linkedAccounts.unlink')}
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={startPending ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                        disabled={startPending}
                        onClick={() => handleStart(row.providerKey)}
                      >
                        {t('settings.linkedAccounts.link')}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <Dialog open={Boolean(unlinkConfirm)} onClose={() => setUnlinkConfirm(null)}>
        <DialogTitle>{t('settings.linkedAccounts.confirmUnlinkTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.linkedAccounts.confirmUnlink', {
              name: unlinkConfirm?.displayName ?? '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkConfirm(null)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleUnlink}>
            {t('settings.linkedAccounts.unlink')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkedAccountsSection;
